import { Shape, Vector3 } from 'three';
import { generateUUID } from 'three/src/math/MathUtils';
import {
  buildActiveMap,
  createExtrudeSettings,
  createRectangleShape,
  getLengthWithKey,
  getWidthMonument,
  SIDES_INFORMATIONS,
} from './common.utils';

export const getPositionWithIndex = (
  key: 'front' | 'back' | 'left' | 'right',
  depth: { front: number; back: number; left: number; right: number },
  pieces: any[],
  customPlatings: any[],
  customBoxes: any[],
  customPlatingsIsActive: { [key: string]: boolean },
  length: number,
  width: number,
  deltaCenterZ: number,
  mode: 'closoir' | 'double closoir' | 'tete',
) => {
  const box = getWidthMonument({ pieces, customElements: customBoxes });

  const deltaX = box.backRight.x - box.backLeft.x - width;
  const deltaZ = mode === 'closoir' ? depth.front / 2 : 0;

  if (box) {
    if (key === 'front') {
      let x = box.frontLeft.x + width / 2 + deltaX / 2;
      let y = box.frontLeft.y;
      let z = box.backLeft.z + length - depth.front - deltaCenterZ;
      return new Vector3(x, y, z);
    }
    if (key === 'back') {
      let x = box.backLeft.x + width / 2 + deltaX / 2;
      let y = box.backLeft.y;
      let z = box.backLeft.z - deltaCenterZ;

      return new Vector3(x, y, z);
    }
    if (key === 'left') {
      let x = box.backLeft.x + deltaX / 2;
      let y = box.frontLeft.y;
      let z = box.backLeft.z + length / 2 - deltaZ - deltaCenterZ;

      if (
        depth.front !== depth.back &&
        mode === 'tete' &&
        customPlatingsIsActive['front'] &&
        customPlatingsIsActive['back']
      ) {
        z += (depth.back - depth.front) / 2;
      }

      return new Vector3(x, y, z);
    }
    if (key === 'right') {
      let x = box.backLeft.x + width - depth.right + deltaX / 2;
      let y = box.frontRight.y;
      let z = box.backRight.z + length / 2 - deltaZ - deltaCenterZ;

      if (
        depth.front !== depth.back &&
        mode === 'tete' &&
        customPlatingsIsActive['front'] &&
        customPlatingsIsActive['back']
      ) {
        z += (depth.back - depth.front) / 2;
      }
      return new Vector3(x, y, z);
    }
  }
};

export const createSoles = (
  pieces: any[],
  customPlatings: any[],
  customBoxes: any[],
  customSoles: any[],
  setCustomSoles: (customSoles: any[]) => void,
  customSize?: {
    length?: number;
    width?: number;
    height?: number;
    holeWidth?: number;
    holeLength?: number;
    poseX?: number;
    poseZ?: number;
    frontDepth?: number;
    hasCustomFrontDepth?: boolean;
    orientation?: 'x' | 'z';
    holeMode?: 'dimension' | 'pose';
    mode?: 'closoir' | 'double closoir' | 'tete';
  },
  checkMinMax?: (length: number) => void,
) => {
  const mode = customSize?.mode ?? 'closoir';
  let soles: any[] = [];

  const monumentBox = getWidthMonument({ pieces, customElements: customBoxes });

  let poseX = customSize?.poseX ?? 0.05;
  let poseZ = customSize?.poseZ ?? 0.05;

  let height = 0.05;
  let monumentWidth = 1.4;
  let monumentLength = 2.4;

  let holeWidth = 0.9;
  let holeLength = 1.9;

  let pieceAboveLenth = monumentBox.frontRight.z - monumentBox.backRight.z;
  let pieceAboveWidth = monumentBox.frontRight.x - monumentBox.frontLeft.x;

  if (pieces.length > 0) {
    monumentWidth = pieceAboveWidth + 0.4;
    monumentLength = pieceAboveLenth + 0.4;

    if (customSize?.holeMode === 'pose') {
      holeWidth = pieceAboveWidth - poseX * 2;
      holeLength = pieceAboveLenth - poseZ * 2;
    } else {
      holeWidth = pieceAboveWidth;
      holeLength = pieceAboveLenth;
    }
  }

  if (customSize?.holeMode === 'dimension') {
    if (customSize?.holeLength !== undefined)
      holeLength = customSize.holeLength;
    if (customSize?.holeWidth !== undefined) holeWidth = customSize.holeWidth;
    poseX = Math.max(0, (pieceAboveWidth - holeWidth) / 2);
    poseZ = Math.max(0, (pieceAboveLenth - holeLength) / 2);
  } else {
    if (customSize?.poseX !== undefined) poseX = customSize.poseX;
    if (customSize?.poseZ !== undefined) poseZ = customSize.poseZ;
  }

  if (customSize) {
    if (customSize.length !== undefined) monumentLength = customSize.length;
    if (customSize.width !== undefined) monumentWidth = customSize.width;
    if (customSize.height !== undefined) height = customSize.height;
  }
  const minDepth = 0.07;

  let frontDepth = 0;

  if (customSize?.hasCustomFrontDepth) {
    frontDepth = customSize.frontDepth ?? 0;
    if (frontDepth < minDepth) {
      frontDepth = minDepth;
    } else if (frontDepth + holeLength + minDepth > monumentLength) {
      frontDepth = monumentLength - holeLength - minDepth;
    }
  } else {
    frontDepth = (monumentLength - holeLength) / 2;
  }

  let backDepth = monumentLength - holeLength - frontDepth;
  let leftDepth = (monumentWidth - holeWidth) / 2;
  let rightDepth = (monumentWidth - holeWidth) / 2;

  if (frontDepth < minDepth) {
    frontDepth = minDepth;
    holeLength = monumentLength - frontDepth * 2;
  }
  if (backDepth < minDepth) {
    backDepth = minDepth;
    holeLength = monumentLength - backDepth * 2;
  }
  if (leftDepth < minDepth) {
    leftDepth = minDepth;
    holeWidth = monumentWidth - leftDepth * 2;
  }
  if (rightDepth < minDepth) {
    rightDepth = minDepth;
    holeWidth = monumentWidth - rightDepth * 2;
  }

  const customSolesIsActive = buildActiveMap(customSoles);

  SIDES_INFORMATIONS.forEach((el, elIndex) => {
    const shape = new Shape();
    const length = getLengthWithKey(
      mode,
      el.key as 'front' | 'back' | 'left' | 'right',
      {
        front: frontDepth,
        back: backDepth,
        left: leftDepth,
        right: rightDepth,
      },
      el.key === 'front' || el.key === 'back' ? monumentWidth : monumentLength,
    );

    checkMinMax?.(length);

    let pieceDepth = 0;

    switch (el.key) {
      case 'front':
        pieceDepth = frontDepth;
        break;
      case 'back':
        pieceDepth = backDepth;
        break;
      case 'left':
        pieceDepth = leftDepth;
        break;
      case 'right':
        pieceDepth = rightDepth;
        break;
    }

    let shapeDetails = {
      display: true,
      hasSlope: false,
      isRotated: el.isRotated,
      length: length,
      height,
      monumentWidth,
      monumentLength,
      holeWidth,
      holeLength,
      poseX,
      poseZ,
      frontDepth,
      depth: pieceDepth,
      holeMode: customSize?.holeMode,
      mode,
      orientation: customSize?.orientation,
      hasCustomFrontDepth: customSize?.hasCustomFrontDepth,
    };

    createRectangleShape(shape, length, shapeDetails.height);

    const deltaCenterZ =
      customSize?.holeMode === 'pose'
        ? backDepth - poseZ
        : backDepth - (pieceAboveLenth - holeLength) / 2;
    soles.push({
      texture: customSoles[elIndex]?.texture,
      granit: customSoles[elIndex]?.granit,
      id: generateUUID(),
      key: el.key,
      shape,
      shapeDetails,
      isActive: customSoles[elIndex] ? customSoles[elIndex].isActive : true,
      position: getPositionWithIndex(
        el.key as 'front' | 'back' | 'left' | 'right',
        {
          front: frontDepth,
          back: backDepth,
          left: leftDepth,
          right: rightDepth,
        },
        pieces,
        customPlatings,
        customBoxes,
        customSolesIsActive,
        monumentLength,
        monumentWidth,
        deltaCenterZ,
        mode,
      ),
      extrudeSettings: createExtrudeSettings(pieceDepth),
    });
  });

  setCustomSoles(soles);
};
